import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Chip,
    Typography,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
} from "@mui/material";
import { useTranslation } from "shared/hooks/useTranslation";
import FormDialog from "shared/ui/FormDialog";
import BalanceDisplay from "shared/money/BalanceDisplay";
import { getAllTransactionsForUser } from "features/budget";
import { effectiveSettlementStatus, settlementCreditorId } from "utils/settlement";
import type { BudgetTransaction } from "types/BudgetTransaction";
import type { GroupTransaction } from "types/GroupTransaction";
import type { UserEntry } from "types/Group";

export interface PoolTransactionHistoryProps {
    open: boolean;
    poolId: string;
    poolName: string;
    username: string;
    token?: string;
    onClose: () => void;
}

type TxWithSource = (BudgetTransaction | GroupTransaction) & {
    source?: "budget" | "group";
    walletDirection?: "out" | "in";
};

function poolIdMatches(txPoolId: unknown, poolId: string): boolean {
    return txPoolId?.toString() === poolId || txPoolId === poolId;
}

function matchesPool(tx: GroupTransaction | BudgetTransaction, poolId: string): boolean {
    if (poolIdMatches(tx.liquidityPoolId, poolId)) return true;
    if (poolIdMatches(tx.sourceLiquidityPoolId, poolId)) return true;
    if (
        tx.type === "settlement" &&
        effectiveSettlementStatus(tx as GroupTransaction) === "confirmed" &&
        poolIdMatches(tx.destinationLiquidityPoolId, poolId)
    ) {
        return true;
    }
    return false;
}

function walletDirectionForPool(tx: GroupTransaction | BudgetTransaction, poolId: string): "out" | "in" {
    if (tx.type === "settlement") {
        const gtx = tx as GroupTransaction;
        if (poolIdMatches(gtx.destinationLiquidityPoolId, poolId) && effectiveSettlementStatus(gtx) === "confirmed") {
            return "in";
        }
        if (poolIdMatches(gtx.sourceLiquidityPoolId, poolId) || poolIdMatches(gtx.liquidityPoolId, poolId)) {
            return "out";
        }
    }
    if ((tx.type ?? "expense") === "income") return "in";
    return "out";
}

const PoolTransactionHistory: React.FC<PoolTransactionHistoryProps> = ({
    open,
    poolId,
    poolName,
    username,
    token,
    onClose,
}) => {
    const { t } = useTranslation();
    const [txs, setTxs] = useState<TxWithSource[]>([]);
    const [groupPeople, setGroupPeople] = useState<UserEntry[]>([]);
    const [loading, setLoading] = useState(true);

    const peopleById = useMemo(() => {
        const map = new Map<string, UserEntry>();
        for (const p of groupPeople) map.set(p.id, p);
        return map;
    }, [groupPeople]);

    useEffect(() => {
        if (!open || !poolId) return;

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await getAllTransactionsForUser(username, token ?? "");
                const budgetTxs = res.budgetTransactions ?? [];
                const groupTxs = res.groupTransactions ?? [];
                setGroupPeople(res.groupPeople ?? []);
                const all: TxWithSource[] = [
                    ...budgetTxs.map((tx) => ({ ...tx, source: "budget" as const })),
                    ...groupTxs.map((tx) => ({ ...tx, source: "group" as const })),
                ];
                const filtered = all
                    .filter((tx) => matchesPool(tx, poolId))
                    .map((tx) => ({ ...tx, walletDirection: walletDirectionForPool(tx, poolId) }));
                filtered.sort((a, b) => {
                    const da = new Date(a.date || 0).getTime();
                    const db = new Date(b.date || 0).getTime();
                    return db - da;
                });
                if (!cancelled) setTxs(filtered);
            } catch {
                if (!cancelled) {
                    setTxs([]);
                    setGroupPeople([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, username, token, poolId]);

    const counterpartyLabel = (tx: TxWithSource) => {
        if (tx.source !== "group" || tx.type !== "settlement") return null;
        const gtx = tx as GroupTransaction;
        const creditorId = settlementCreditorId(gtx);
        const counterpartyId = tx.walletDirection === "in" ? gtx.payer : creditorId;
        if (!counterpartyId) return null;
        const person = peopleById.get(counterpartyId);
        const name = person?.displayName ?? counterpartyId;
        return tx.walletDirection === "in"
            ? t("wallet.receivedFrom", { name })
            : t("wallet.paidTo", { name });
    };

    const settlementStatusLabel = (tx: TxWithSource) => {
        if (tx.type !== "settlement") return null;
        const status = effectiveSettlementStatus(tx as GroupTransaction);
        if (status === "pending_confirmation") return t("balances.settlementStatusPending");
        if (status === "rejected") return t("balances.settlementStatusRejected");
        if (status === "confirmed") return t("balances.settlementStatusConfirmed");
        return null;
    };

    return (
        <FormDialog
            open={open}
            onClose={onClose}
            title={t("wallet.transactionsFor", { poolName })}
            maxWidth="sm"
        >
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : txs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("wallet.noTransactionsForPool")}
                </Typography>
            ) : (
                <List dense>
                    {txs.map((tx) => {
                        const isIncome = tx.walletDirection === "in";
                        const groupLabel = tx.groupName ? t("wallet.fromGroup", { group: tx.groupName }) : null;
                        const counterparty = counterpartyLabel(tx);
                        const statusLabel = settlementStatusLabel(tx);

                        return (
                            <ListItem key={`${tx.source}-${tx._id}`} sx={{ px: 0, alignItems: "flex-start" }}>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
                                            <span>{tx.name}</span>
                                            {statusLabel ? (
                                                <Chip size="small" label={statusLabel} variant="outlined" />
                                            ) : null}
                                        </Box>
                                    }
                                    secondary={
                                        <>
                                            {new Date(tx.date || 0).toLocaleDateString(undefined, { dateStyle: "short" })}
                                            {groupLabel ? ` · ${groupLabel}` : null}
                                            {counterparty ? ` · ${counterparty}` : null}
                                        </>
                                    }
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {isIncome ? "+" : "-"}
                                    </Typography>
                                    <BalanceDisplay
                                        amount={tx.amount ?? 0}
                                        currency={tx.currency ?? "ARS"}
                                        isPositive={isIncome}
                                        variant="body2"
                                        showCurrencyCode={false}
                                        keepOriginalCurrency
                                    />
                                </Box>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </FormDialog>
    );
};

export default PoolTransactionHistory;
